import { Button } from "@ttc3k/trekker";
import { format, parse } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { NoteManyQuery } from "gql/generated";
import _ from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Collapsible,
  EmptyState,
  NoteCard,
  TimelineGroupLayout,
} from "shared/components";
import { useNotesContext } from "shared/contextProviders";
import { DATE_PATTERN_SIMPLE } from "shared/utils";
import { Flex } from "styled-system/jsx";
import { Variants } from "framer-motion";

export type TimelineNoteItem = {
  _id: string;
  groupByDate: string;
  note: NoteManyQuery["noteMany"][number];
};

export type NoteTimelineGroup = {
  groupByDate: string;
  items: TimelineNoteItem[];
};

export type TimelineNotesViewProps = {
  isCreatingNote: boolean;
  setIsCreatingNote: (value: boolean) => void;
  suggestionsPortalHost?: HTMLElement | null;
};

const formatGroupDate = (groupByDate: string, t: (key: string) => string) => {
  const itemDate = format(
    parse(groupByDate, DATE_PATTERN_SIMPLE, new Date()),
    DATE_PATTERN_SIMPLE,
  );
  const today = format(new Date(), DATE_PATTERN_SIMPLE);
  return itemDate === today
    ? t("core:TODAY")
    : format(toZonedTime(groupByDate, "UTC"), "MMM d");
};

export const TimelineNotesView = ({
  isCreatingNote,
  setIsCreatingNote,
  suggestionsPortalHost,
}: TimelineNotesViewProps) => {
  const { t } = useTranslation();
  const { notes, viewOnly } = useNotesContext();

  const openNotes = useMemo(
    () => (notes ?? []).filter(note => !note.isArchived),
    [notes],
  );

  const timelineGroups: NoteTimelineGroup[] = useMemo(() => {
    const mapped = openNotes.map(note => {
      const groupByDate = note.noteDateISO
        ? format(toZonedTime(note.noteDateISO, "UTC"), DATE_PATTERN_SIMPLE)
        : format(note.createdDateISO ?? "", DATE_PATTERN_SIMPLE);
      return { _id: note._id, groupByDate, note };
    });
    const byDate = _.groupBy(mapped, "groupByDate");
    const groups = Object.entries(byDate).map(([groupByDate, items]) => ({
      groupByDate,
      items,
    }));
    return _.orderBy(groups, "groupByDate", "desc");
  }, [openNotes]);

  const handleNoteSaveSuccess = () => setIsCreatingNote(false);

  return (
    <Flex flexDir="column" gap="300">
      {!viewOnly && (
        <Button
          visual="unstyled"
          fontSize="16px"
          fontWeight="500"
          color="text.accent.blue.mid"
          _hover={{ color: "text.accent.blue.dark" }}
          _focusVisible={{ color: "text.accent.blue.dark" }}
          onClick={() => setIsCreatingNote(true)}
          alignSelf="flex-start"
        >
          {t("core:NOTES.CREATE")}
        </Button>
      )}

      <Collapsible
        open={isCreatingNote}
        additionalVariantProps={
          {
            open: { display: "block" },
            closed: { display: "none" },
          } as Variants
        }
      >
        {isCreatingNote && (
          <NoteCard
            initialMode="edit"
            onSaveSuccess={handleNoteSaveSuccess}
            onCancel={() => setIsCreatingNote(false)}
            suggestionsPortalHost={suggestionsPortalHost}
          />
        )}
      </Collapsible>

      {!isCreatingNote && openNotes.length === 0 && (
        <EmptyState
          text={t("core:NOTES.EMPTY")}
          p="400"
          minH="120px"
        />
      )}

      {!isCreatingNote &&
        timelineGroups.length > 0 &&
        timelineGroups.map(group => (
          <TimelineGroupLayout
            key={group.groupByDate}
            formattedDateString={formatGroupDate(group.groupByDate, t)}
          >
            {group.items.map(item => (
              <NoteCard
                key={item.note._id}
                initialMode="view"
                note={item.note}
                suggestionsPortalHost={suggestionsPortalHost}
              />
            ))}
          </TimelineGroupLayout>
        ))}
    </Flex>
  );
};
